// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.
import { AssetsImageProps } from "../Asset";
import { GridSize } from "@material-ui/core/Grid";
import { ReactNode } from "react";

export interface SelectOption {
  value: string;
  label?: string | React.ReactNode;
  name: string | React.ReactNode;
  default?: boolean;
  asset?: AssetsImageProps;
  grid?: {
    xs?: GridSize;
    sm?: GridSize;
  };
}

export type QuestionTypes =
  | "hidden"
  | "text"
  | "select"
  | "email"
  | "password"
  | "tel"
  | "checkbox"
  | "buttongroup"
  | "radio"
  | "radiobutton"
  | "date";

// Describes an input field.
export type Question =
  | TextQuestion
  | SelectQuestion
  | DropDownSelectQuestion
  | RadioButtonQuestion
  | BasicQuestion
  | DateQuestion;

export interface QuestionBase {
  // type of the input field
  type: QuestionTypes;
  // name of the input field
  name: string;

  // If specified, the value to use for the `autocomplete` property on the input field
  autofill?: string;
  // potential error being displayed.
  error?: string;
  // icon to show on the left
  icon?: string;
  // title above the input
  label?: string | React.ReactNode;
  // Is the field mandatory for the form
  mandatory?: boolean;
  // placeholder value of the input field
  placeholder?: string;
  // validator on change
  validators?: Array<QuestionValidator>;
  // value of the input. Might need to change to support checkbox and list.
  value?: string;
}

export interface BasicQuestion extends QuestionBase {
  type: "hidden" | "checkbox";
}

export interface TextQuestion extends QuestionBase {
  type: "text" | "email" | "password" | "tel";

  // format input value on change
  formatter?: (
    text: string,
    questions: { [key: string]: Question },
    currentQuestion: Question
  ) => string;
  typeahead?: {
    callFunction: (
      question: Question,
      resolvePromise: (question: Question) => void | PromiseLike<void>,
      rejectPromise: (err: Error) => void
    ) => Promise<void> | void;
    values: Array<string>;
    blob?: any; // holds any type of information you may need.
  };
  // classNames to add to sub-components
  classNames?: {
    textField?: string;
  };

  endAdornment?: ReactNode;
}

export function isTextQuestion(q: Question): q is TextQuestion {
  return ["text", "email", "password", "tel"].includes(q.type);
}

export interface DateQuestion extends QuestionBase {
  type: "date";
  minDate?: Date;
  maxDate?: Date;
  disableFuture?: boolean;
}

export function isDateQuestion(q: Question): q is DateQuestion {
  return q.type === "date";
}

export interface SelectQuestionBase extends QuestionBase {
  // options for a select component.
  selectOptions?: Array<SelectOption>;
}

export interface SelectQuestion extends SelectQuestionBase {
  type: "radio" | "buttongroup";
}

export interface DropDownSelectQuestion extends SelectQuestionBase {
  type: "select";

  // Use a native HTML select element instead of the material-ui reimplementation.
  native?: boolean;
}

export interface RadioButtonQuestion extends SelectQuestionBase {
  type: "radiobutton";

  // use a grid to layout children
  gridLayout?: boolean;
  // classNames to add to sub-components
  classNames?: {
    button?: string;
  };
}

export function isSelectQuestion(q: Question): q is SelectQuestion {
  return ["select", "radio", "buttongroup", "radiobutton"].includes(q.type);
}

export interface QuestionnaireRenderComponents {
  [inputType: string]: (props: QuestionnaireInputProps) => JSX.Element;
}

// returns a potential error or null if validated
export interface QuestionValidator {
  (
    text: string | null,
    questions: { [key: string]: Question },
    currentQuestion: Question
  ): string | undefined;
}

export interface QuestionnaireInputProps {
  question: Question;
  onChange: (event: React.ChangeEvent | React.MouseEvent | DummyEvent) => void;
}

export interface DummyEvent {
  currentTarget: {
    name: string;
    value: string | null;
  };
}
